import React, { useCallback } from 'react'
import { JsonFileUpload } from '@/common/components/JsonFileUpload.tsx'
import { PmTermSheetParam } from '@/pages/document-autofill/term-sheets/pm-term-sheet.tsx'
import { useFormContext } from 'react-hook-form'
import { useSearchParams } from 'react-router'

interface TermSheetImportProps {
  isTermSheet?: boolean
}

export const TermSheetImport: React.FC<TermSheetImportProps> = ({ isTermSheet = false }) => {
  const [_, setSearchParams] = useSearchParams()

  const { setValue } = useFormContext()

  const onFileImport = useCallback((data: PmTermSheetParam) => {
    if (isTermSheet && data.meta) {
      if (data.meta) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev)
          if (data.meta?.type) {
            next.set('type', data.meta.type)
          }
          if (data.meta?.region) {
            next.set('region', data.meta.region)
          }
          if (data.meta?.candidateType) {
            next.set('candidateType', data.meta.candidateType)
          }
          if (data.meta?.isHiringManagerPrincipal) {
            next.set('isHiringManagerPrincipal', data.meta.isHiringManagerPrincipal ? 'true' : 'false')
          }

          return next
        })
      }
    }
    setValue('personalInfo.firstName', data.personalInfo.firstName)
    setValue('personalInfo.lastName', data.personalInfo.lastName)
    setValue('personalInfo.fullName', data.personalInfo.fullName)
    setValue('employmentDetails.startDate', data.employmentDetails.startDate)
    setValue('employmentDetails.position', data.employmentDetails.position)
    setValue('employmentDetails.mandate', data.riskParameters.mandate)
    setValue('employmentDetails.location', data.employmentDetails.location)
    setValue('employmentDetails.salary', data.financialTerms.salary)
    setValue('employmentDetails.centerBookPayoutRate', data.financialTerms.centerBookPayoutRate)
    if (data.financialTerms.relocation && data.financialTerms.relocation.amount > 0) {
      setValue('relocation.expenseCap', data.financialTerms.relocation)
    }
    if (data.financialTerms.signingBonus) {
      setValue('bonus.signingBonus.amount', data.financialTerms.signingBonus?.amount)
      setValue('bonus.signingBonus.currency', data.financialTerms.signingBonus?.currency)
      setValue('bonus.signingBonus.payableOn', data.financialTerms.signingBonus?.payableOn)
      setValue(
        'bonus.signingBonus.subjectToPrevIncomeProof',
        data.financialTerms.signingBonus?.subjectToPrevIncomeProof,
      )
    }
    setValue('bonus.additionalBonus', data.financialTerms.additionalBonus)
    setValue('bonus.additionalBonus.paymentYears', data.financialTerms.additionalBonus?.paymentYears)
    setValue('bonus.performanceBonus', data.financialTerms.performanceBonus)
    setValue('deferredProgram.fundName', data.deferredProgram.fundName)
    setValue('teamHiring.firstAnalystExpenseCap', data.teamHiring.expenseCap)
    setValue('teamHiring.firstAnalystFreeUntilBpThresholdMm', data.teamHiring.hiringLimitBpThresholdMm)
    setValue('teamHiring.expenseCap', data.teamHiring.expenseCap)
    setValue('teamHiring.initialHeadcount', data.teamHiring.initialHeadcount)
    setValue('teamHiring.hiringLimitDate', data.teamHiring.hiringLimitDate)
    setValue('teamHiring.hiringLimitBpThresholdMm', data.teamHiring.hiringLimitBpThresholdMm)
    setValue('riskParameters.buyingPower', data.riskParameters.buyingPower)
    setValue(
      'riskParameters.buyingPowerRequireManagementApproval',
      data.riskParameters.buyingPowerRequireManagementApproval,
    )
    setValue('riskParameters.netLimitRate', data.riskParameters.netLimitRate)
    setValue('riskParameters.netLimitType', data.riskParameters.netLimitType)
    setValue('riskParameters.netLimitTypeOther', data.riskParameters.netLimitTypeOther)
    setValue('riskParameters.maximumPositionRate', data.riskParameters.maximumPositionRate)
    setValue('riskParameters.maximumPositionType', data.riskParameters.maximumPositionType)
    setValue('riskParameters.maximumPositionTypeOther', data.riskParameters.maximumPositionTypeOther)
    setValue('riskParameters.marketCapitalizationLimitM', data.riskParameters.marketCapitalizationLimitM)
    setValue('riskParameters.liquidityLimitRate1', data.riskParameters.liquidityLimitRate1)
    setValue('riskParameters.liquidityLimitDays1', data.riskParameters.liquidityLimitDays1)
    setValue('riskParameters.liquidityLimitRate2', data.riskParameters.liquidityLimitRate2)
    setValue('riskParameters.liquidityLimitDays2', data.riskParameters.liquidityLimitDays2)
    setValue('riskParameters.speedBumpBpLimitRate', data.riskParameters.speedBumpBpLimitRate)
    setValue('riskParameters.speedBumpBpCutRate', data.riskParameters.speedBumpBpCutRate)
    setValue('riskParameters.downAndOutBpRate', data.riskParameters.downAndOutBpRate)
    setValue('riskParameters.specialNotes', data.riskParameters.specialNotes)
    setValue('riskParameters.mandate', data.riskParameters.mandate)
    setValue('riskParameters.mandateDetails', data.riskParameters.mandateDetails)
    setValue('riskParameters.countryLimits', data.riskParameters.countryLimits)
    setValue('riskParameters.netLimitRate2', data.riskParameters.netLimitRate2)
    setValue('riskParameters.netLimitType2', data.riskParameters.netLimitType2)
    setValue('riskParameters.netLimitTypeOther2', data.riskParameters.netLimitTypeOther2)

    // Term sheet schema
    if (isTermSheet) {
      setValue('financialTerms.salary', data.financialTerms.salary)
      setValue('financialTerms.centerBookPayoutRate', data.financialTerms.centerBookPayoutRate)
      setValue('financialTerms.relocation', data.financialTerms.relocation)

      if (data.financialTerms.signingBonus) {
        setValue('financialTerms.signingBonus.amount', data.financialTerms.signingBonus?.amount)
        setValue('financialTerms.signingBonus.currency', data.financialTerms.signingBonus?.currency)
        setValue('financialTerms.signingBonus.payableOn', data.financialTerms.signingBonus?.payableOn)
        setValue(
          'financialTerms.signingBonus.subjectToPrevIncomeProof',
          data.financialTerms.signingBonus?.subjectToPrevIncomeProof,
        )
      }
      setValue('financialTerms.additionalBonus', data.financialTerms.additionalBonus)
      setValue('financialTerms.additionalBonus.paymentYears', data.financialTerms.additionalBonus?.paymentYears)
      setValue('financialTerms.performanceBonus', data.financialTerms.performanceBonus)
      setValue('teamHiring.expenseCap', data.teamHiring.expenseCap)
      setValue('teamHiring.hiringLimitBpThresholdMm', data.teamHiring.hiringLimitBpThresholdMm)
      setValue('teamHiring.initialHeadcount', data.teamHiring.initialHeadcount)
      setValue('teamHiring.hiringLimitDate', data.teamHiring.hiringLimitDate)

      setValue('riskParameters.hasGmv', data.riskParameters.hasGmv)
      setValue('riskParameters.hasIntellectualProperty', data.riskParameters.hasIntellectualProperty)
    }
  }, [isTermSheet, setValue])

  return (
    <>
      <JsonFileUpload label="Import Term Sheet" updateFormWithJson={onFileImport} />
    </>
  )
}